import { inject, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { EMPTY, from, lastValueFrom, map, Observable, of, shareReplay, switchMap, timer } from 'rxjs';
import { environment } from '@environment/environment';
import { FirebaseAuthService } from '@services/utility-services/firebase-auth.service';
import { LogoutService } from '@services/utility-services/logout.service';
import { ToastService } from '@shared/toasts/toast.service';
import { getDevLoginCode } from '@src/app/features/auth/utils/dev-login-link.util';

/** Response of bpp-auth `POST api/auth-dashboard/dev-login/exchange` (not in the generated client). */
interface DevLoginExchangeResultDto {
  firebaseCustomToken: string;
}

/** Why a dev login did not go through. */
export type DevLoginFailure = 'codeInvalid' | 'failed';

/** Translation key of the toast per failure. */
const DEV_LOGIN_FAILURE_KEY: Record<DevLoginFailure, string> = {
  codeInvalid: 'auth.error.devLoginCodeInvalid',
  failed: 'auth.error.signInFailed'
};

/**
 * Developer login as a Stella person: the bpp-auth dashboard opens `<scheme>://dev-login?code=…`
 * on the developer's phone. The app logs out whoever is signed in, trades the
 * one-time code for a Firebase custom token and signs in with it. The session ends at the token's
 * `devLoginExpiresAt` claim (1 h after minting).
 *
 * Routing after the sign-in is the regular LoadingService pipeline reacting to the new user.
 */
@Injectable({
  providedIn: 'root'
})
export class DevLoginService {
  private readonly http = inject(HttpClient);
  private readonly firebaseAuthService = inject(FirebaseAuthService);
  private readonly logoutService = inject(LogoutService);
  private readonly toastService = inject(ToastService);

  /** When the dev session of whoever is signed in ends (unix seconds); null for a regular session or nobody. */
  private readonly devLoginExpiresAt$: Observable<number | null> = this.firebaseAuthService.currentUser$.pipe(
    switchMap(user => user ? from(this.firebaseAuthService.getDevLoginExpiresAtAsync()) : of(null)),
    shareReplay({ bufferSize: 1, refCount: false })
  );

  /** Drives the "Support-Sitzung" banner and the blocked AGB acceptance. */
  public readonly isDevSession: Signal<boolean> = toSignal(
    this.devLoginExpiresAt$.pipe(map((expiresAt: number | null) => expiresAt !== null)),
    { initialValue: false }
  );

  /**
   * Takes over `url` if it is the dev-login link and returns true; false leaves it to the other
   * link handlers.
   */
  public handleUrl(url: string): boolean {
    const code = getDevLoginCode(url);

    if (!code) {
      return false;
    }

    void this.signInFromLinkAsync(code);

    return true;
  }

  /**
   * Exchanges the code and signs in with the custom token. Resolves with the failure, or null once
   * signed in. Someone already signed in is logged out first, whatever the code turns out to be.
   */
  public async signInWithCodeAsync(code: string): Promise<DevLoginFailure | null> {
    if (await this.firebaseAuthService.getCurrentUser()) {
      await this.logoutService.logoutAsync();
    }

    let customToken: string;

    try {
      const result = await lastValueFrom(this.http.post<DevLoginExchangeResultDto>(
        `${ environment.bppAuth }/api/auth-dashboard/dev-login/exchange`,
        { code }
      ));
      customToken = result.firebaseCustomToken;
    } catch (error) {
      const status = (error as HttpErrorResponse)?.status;
      // Never log the code or the token
      console.error('Dev login: code exchange failed with status', status);
      return status === 404 ? 'codeInvalid' : 'failed';
    }

    try {
      await this.firebaseAuthService.signInWithCustomTokenAsync(customToken);
    } catch (error) {
      console.error('Dev login: sign-in with the custom token failed', (error as { code?: string })?.code);
      return 'failed';
    }

    return null;
  }

  /**
   * Ends a dev session at `devLoginExpiresAt` — also when the app starts with a session that has
   * already run out. A regular session is not affected.
   */
  public startSessionWatch(): void {
    this.devLoginExpiresAt$.pipe(
      // switchMap: a new user (or the sign-out) drops the previous session's timer
      switchMap(expiresAt => expiresAt !== null ? timer(new Date(expiresAt * 1000)) : EMPTY)
    ).subscribe(() => this.logoutService.logout());
  }

  private async signInFromLinkAsync(code: string): Promise<void> {
    const failure = await this.signInWithCodeAsync(code);

    if (failure) {
      void this.toastService.presentToastAsync(DEV_LOGIN_FAILURE_KEY[failure]);
    }
  }
}
